import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { fmtErr } from "../../lib/api";
import { toast } from "sonner";
import { BadgeCheck, Palette, Upload, Trash2, Copy } from "lucide-react";

const inputCls = "mt-1 w-full bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-900 outline-none";
const API_BASE = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_BRAND = "#10314b";

export default function DeskSettings() {
  const [company, setCompany] = useState(null);
  const [form, setForm] = useState({ name: "", remote_tool_link: "" });
  const [brand, setBrand] = useState("");
  const [slug, setSlug] = useState("");
  const [sla, setSla] = useState({ P1: 4, P2: 8, P3: 24, P4: 72 });
  const [awa, setAwa] = useState({ enabled: false, mode: "round_robin" });
  const [codes, setCodes] = useState("");
  const [busy, setBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const pollRef = useRef(0);

  const load = useCallback(() => {
    api.get("/helpdesk/company").then((r) => {
      setCompany(r.data);
      setForm({ name: r.data.name, remote_tool_link: r.data.remote_tool_link || "" });
      setBrand(r.data.brand_color || "");
      setSlug(r.data.portal_slug || "");
      setSla(r.data.sla_hours);
      setAwa(r.data.awa);
      setCodes((r.data.resolution_codes || []).join("\n"));
    }).catch((e) => toast.error(fmtErr(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    const poll = async () => {
      if (pollRef.current++ > 8) return;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { toast.success("Subscription active — thank you!"); setParams({}); load(); return; }
      } catch { /* retry */ }
      setTimeout(poll, 2000);
    };
    poll();
  }, [params, setParams, load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      const sla_hours = Object.fromEntries(Object.entries(sla).map(([k, v]) => [k, parseFloat(v)]));
      const resolution_codes = codes.split("\n").map((c) => c.trim()).filter(Boolean);
      await api.put("/helpdesk/company", { ...form, sla_hours, awa, resolution_codes });
      toast.success("Settings saved");
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/helpdesk/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(fmtErr(e)); setBusy(false); }
  };

  const saveBranding = async () => {
    try {
      await api.put("/helpdesk/company", { brand_color: brand || "", portal_slug: slug || undefined });
      toast.success("Branding saved — reloading workspace");
      setTimeout(() => window.location.reload(), 700);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/helpdesk/company/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo uploaded — reloading workspace");
      setTimeout(() => window.location.reload(), 700);
    } catch (e) { toast.error(fmtErr(e)); setLogoBusy(false); }
  };

  const removeLogo = async () => {
    try {
      await api.put("/helpdesk/company", { remove_logo: true });
      toast.success("Logo removed — reloading workspace");
      setTimeout(() => window.location.reload(), 700);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const sub = company?.subscription;
  return (
    <div data-testid="desk-settings-page" className="max-w-2xl">
      <h1 className="text-2xl font-bold font-heading mb-6">Settings</h1>

      <div className="bg-white border border-neutral-200 rounded-2xl p-6 mb-6" data-testid="subscription-card">
        <h3 className="font-semibold font-heading text-sm flex items-center gap-2 mb-2">
          <BadgeCheck size={16} className={sub?.active ? "text-emerald-600" : "text-red-500"} /> Subscription
        </h3>
        {sub && (
          <p className="text-sm text-neutral-600">
            {sub.status === "trial" ? `Free trial — ${sub.days_left ?? 0} day${sub.days_left === 1 ? "" : "s"} left.` :
              sub.active ? `Active until ${new Date(sub.access_until).toLocaleDateString()} (${sub.days_left} days left).` :
              "Your subscription has expired."}
          </p>
        )}
        <button data-testid="subscribe-button" onClick={subscribe} disabled={busy}
          className="mt-4 px-6 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium disabled:opacity-50">
          {busy ? "Redirecting..." : `${sub?.status === "trial" ? "Subscribe" : "Renew"} — $${company?.monthly_price?.toFixed(2) || "99.00"}/mo`}
        </button>
      </div>

      <div className="bg-white border border-neutral-200 rounded-2xl p-6 mb-6" data-testid="branding-card">
        <h3 className="font-semibold font-heading text-sm flex items-center gap-2 mb-1"><Palette size={16} className="text-slate-400" /> Branding</h3>
        <p className="text-xs text-neutral-400 mb-4">Your logo and brand color are applied across your whole workspace — for agents and end users.</p>
        <div className="flex flex-wrap items-center gap-6">
          <div>
            <p className="text-xs font-semibold text-neutral-600 mb-2">Logo</p>
            <div className="flex items-center gap-3">
              <div className="w-24 h-14 border border-dashed border-neutral-300 rounded-lg flex items-center justify-center bg-neutral-50 overflow-hidden">
                {company?.logo_url
                  ? <img src={`${API_BASE}${company.logo_url}`} alt="logo" className="max-h-12 max-w-[88px] object-contain" data-testid="branding-logo-preview" />
                  : <span className="text-[10px] text-neutral-400">No logo</span>}
              </div>
              <label className="inline-flex items-center gap-1.5 px-3 py-2 border border-neutral-300 rounded-md text-xs font-medium hover:bg-neutral-50 cursor-pointer">
                <Upload size={13} /> {logoBusy ? "Uploading..." : "Upload"}
                <input data-testid="branding-logo-input" type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden"
                  onChange={(e) => uploadLogo(e.target.files?.[0])} disabled={logoBusy} />
              </label>
              {company?.logo_url && (
                <button data-testid="branding-logo-remove" onClick={removeLogo} title="Remove logo"
                  className="p-2 border border-red-200 text-red-600 rounded-md hover:bg-red-50"><Trash2 size={13} /></button>
              )}
            </div>
            <p className="text-[10px] text-neutral-400 mt-1.5">PNG, JPG, WEBP or SVG — max 2MB.</p>
          </div>
          <div>
            <p className="text-xs font-semibold text-neutral-600 mb-2">Brand color</p>
            <div className="flex items-center gap-2">
              <input data-testid="branding-color-picker" type="color" value={brand || DEFAULT_BRAND}
                onChange={(e) => setBrand(e.target.value)} className="w-10 h-10 rounded-md border border-neutral-300 cursor-pointer p-0.5" />
              <input data-testid="branding-color-hex" value={brand} placeholder={DEFAULT_BRAND}
                onChange={(e) => setBrand(e.target.value)} className={inputCls + " mt-0 w-28 font-mono text-xs"} />
              {brand && (
                <button data-testid="branding-color-reset" onClick={() => setBrand("")}
                  className="text-xs text-neutral-400 hover:text-neutral-700 underline">Reset</button>
              )}
            </div>
            <div className="flex items-center gap-2 mt-3">
              <span className="px-4 py-1.5 rounded-md text-white text-xs font-medium" style={{ background: brand || DEFAULT_BRAND }}>Button preview</span>
              <button data-testid="branding-save-button" onClick={saveBranding}
                className="px-4 py-1.5 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-xs font-medium">Save branding</button>
            </div>
          </div>
        </div>
        <div className="mt-5 pt-4 border-t border-neutral-100">
          <p className="text-xs font-semibold text-neutral-600 mb-1">Branded login portal</p>
          <p className="text-[11px] text-neutral-400 mb-2">Share this URL with your team — they'll see your logo and colors before signing in.</p>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-neutral-500 font-mono">{window.location.origin}/portal/</span>
            <input data-testid="portal-slug-input" value={slug} onChange={(e) => setSlug(e.target.value)}
              className={inputCls + " mt-0 w-44 font-mono text-xs"} placeholder="your-company" />
            <button data-testid="portal-copy-button" title="Copy portal URL"
              onClick={async () => {
                const url = `${window.location.origin}/portal/${slug}`;
                try { await navigator.clipboard.writeText(url); toast.success("Portal URL copied"); }
                catch { toast.error(url); }
              }}
              className="p-2 border border-neutral-300 rounded-md hover:bg-neutral-50"><Copy size={13} /></button>
            <a data-testid="portal-open-link" href={`/portal/${slug}`} target="_blank" rel="noreferrer"
              className="text-xs text-neutral-500 underline hover:text-neutral-800">Preview</a>
          </div>
          <p className="text-[10px] text-neutral-400 mt-1.5">Lowercase letters, numbers and hyphens. Saved with "Save branding".</p>
        </div>
      </div>

      <form onSubmit={save} className="bg-white border border-neutral-200 rounded-2xl p-6">
        <h3 className="font-semibold font-heading text-sm mb-3">Workspace</h3>
        <label className="text-sm font-medium text-neutral-700">Company name</label>
        <input data-testid="company-name-input" required minLength={2} value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} />
        <label className="text-sm font-medium text-neutral-700 mt-3 block">Remote tool link (TeamViewer / AnyDesk etc.)</label>
        <input data-testid="remote-tool-input" value={form.remote_tool_link} placeholder="https://..."
          onChange={(e) => setForm((f) => ({ ...f, remote_tool_link: e.target.value }))} className={inputCls} />
        <p className="text-xs text-neutral-400 mt-1.5">Shown on every ticket next to the built-in screen share so agents can launch your preferred tool.</p>

        <h3 className="font-semibold font-heading text-sm mt-6 mb-1 pt-5 border-t border-neutral-100">SLA targets</h3>
        <p className="text-xs text-neutral-400 mb-3">Hours until a ticket of each priority breaches its SLA.</p>
        <div className="grid grid-cols-4 gap-3" data-testid="sla-settings">
          {["P1", "P2", "P3", "P4"].map((p) => (
            <div key={p}>
              <label className="text-xs font-semibold text-neutral-600">{p} (hrs)</label>
              <input data-testid={`sla-input-${p}`} type="number" step="0.5" min="0.5" required value={sla[p]}
                onChange={(e) => setSla((s) => ({ ...s, [p]: e.target.value }))} className={inputCls} />
            </div>
          ))}
        </div>

        <h3 className="font-semibold font-heading text-sm mt-6 mb-1 pt-5 border-t border-neutral-100">Advanced work assignment</h3>
        <p className="text-xs text-neutral-400 mb-3">Automatically route new tickets to available agents.</p>
        <div className="flex flex-wrap items-center gap-4" data-testid="awa-settings">
          <label className="text-sm text-neutral-700 flex items-center gap-2">
            <input data-testid="awa-enabled-checkbox" type="checkbox" checked={awa.enabled}
              onChange={(e) => setAwa((a) => ({ ...a, enabled: e.target.checked }))} />
            Enable auto-assignment
          </label>
          <select data-testid="awa-mode-select" value={awa.mode} disabled={!awa.enabled}
            onChange={(e) => setAwa((a) => ({ ...a, mode: e.target.value }))}
            className={inputCls + " mt-0 w-auto disabled:opacity-50"}>
            <option value="round_robin">Round robin</option>
            <option value="least_loaded">Least loaded agent</option>
          </select>
        </div>
        <p className="text-xs text-neutral-400 mt-2">Only agents marked <b>Available</b> (toggle in the top bar) receive auto-assignments.</p>

        <h3 className="font-semibold font-heading text-sm mt-6 mb-1 pt-5 border-t border-neutral-100">Resolution codes</h3>
        <p className="text-xs text-neutral-400 mb-2">One per line — agents must pick one when resolving or closing a ticket.</p>
        <textarea data-testid="resolution-codes-input" rows={6} required value={codes}
          onChange={(e) => setCodes(e.target.value)} className={inputCls + " font-mono text-xs"} />

        <button data-testid="settings-save-button" className="mt-5 px-6 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">Save</button>
      </form>
    </div>
  );
}
